// src/i18n.js
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const resources = {
  en: {
    translation: {
      nav: {
        home: "Home",
        login: "Login",
        register: "Register",
        logout: "Logout",
        myProperties: "My Properties",
        addProperty: "Add property",
        admin: "Admin",
      },

      home: {
        title: "Find your stay in Greece",
        cityPlaceholder: "City / area…",
        maxPricePlaceholder: "Max €/night",
        chips: {
          all: "All",
          wifi: "Wi-Fi",
          parking: "Parking",
          pool: "Pool",
          new: "New",
          saved: "Saved",
          more: "More",
        },
        sort: {
          aria: "Sort",
          recommended: "Recommended",
          priceAsc: "Price: Low to High",
          ratingDesc: "Rating: High to Low",
          newest: "Newest",
        },
        view: {
          list: "List",
          map: "Map",
        },
        availableStays: "Available stays",
        resultOne: "result",
        resultMany: "results",
        empty: {
          title: "No stays found",
          sub: "Try another location, filter, or increase max price.",
        },
        map: {
          title: "Map (placeholder)",
          sub: "Next step: add real map (Google Maps / Mapbox) + pins.",
        },
      },

      footer: {
        privacy: "Privacy Policy",
      },

      login: {
        title: "Login",
        subtitle: "Welcome back. Please sign in to continue.",
        emailPlaceholder: "Email",
        passwordPlaceholder: "Password",
        loggingIn: "Logging in...",
        readyTitle: "Ready",
        enterEmailPasswordTitle: "Enter email and password.",
        noAccount: "Don't have an account?",
        registerLink: "Register",
        errors: {
          enterEmailPassword: "Enter email and password.",
          invalidEmailPassword: "Invalid email or password.",
          checkInput: "Please check your input.",
          tooManyAttempts: "Too many attempts. Please try again later.",
          serverError: "Server error. Please try again.",
          loginFailed: "Login failed.",
          missingUserPayload: "Login succeeded but user payload is missing.",
          cannotConnect: "Cannot connect to server. Please try again.",
        },
      },

      register: {
        title: "Register",
        subtitle: "Create an account to save and reserve stays.",
        emailPlaceholder: "Email",
        passwordPlaceholder: "Password (min 8, 1 uppercase, 1 number)",
        registering: "Registering...",
        readyTitle: "Ready",
        passwordRuleTitle:
          "Password must be 8+ chars, include 1 uppercase and 1 number (letters/digits only).",
        haveAccount: "Already have an account?",
        loginLink: "Login",
        errors: {
          enterEmailPassword: "Enter email and password.",
          passwordRule:
            "Password must be at least 8 characters and include at least one CAPITAL letter and one number (letters/digits only).",
          registrationFailed: "Registration failed",
          missingUserPayload: "Registration succeeded but user payload is missing.",
          cannotConnect: "Cannot connect to server. Please try again.",
        },
      },

      addProperty: {
        title: "Create a new listing",
        subtitle: "Airbnb-style editor • Greece only 🇬🇷",

        // ✅ sections (existing + NEW)
        section: {
          title: "Title",
          basics: "Basics",
          location: "Location",
          description: "Description",
          rating: "Rating",
          amenities: "Amenities",
          houseRules: "House rules",
          pricingExtras: "Pricing extras",
          rooms: "Rooms",
          photos: "Photos",
          previewTitle: "Listing preview",
          previewSubtitle: "What guests will roughly see",
        },

        placeholders: {
          title: "e.g. Cozy apartment near the beach",
          location: "Search in Greece: city, area, address…",
          description: "Tell guests what makes your place special…",
          rating: "1-5",
          price: "e.g. 80",
        },

        tips: {
          map: "Tip: Search a place OR click on the map to drop a pin. Only Greece is allowed.",
        },

        // ✅ labels (existing + NEW)
        labels: {
          selected: "Selected",
          room: "Room",
          roomType: "Room type",
          pricePerNight: "Price / night (€)",
          availableDates: "Available dates",
          pickMultiple: "Pick multiple days",
          addAnotherRoom: "Add another room",
          addPhotos: "Add photos",
          remove: "Remove",
          startingFrom: "Starting from",
          perNight: "/ night",
          rooms: "Rooms",
          photos: "Photos",

          // NEW: basics fields
          propertyType: "Property type",
          maxGuests: "Max guests",
          bedrooms: "Bedrooms",
          bathrooms: "Bathrooms",

          // NEW: pricing extras labels (if you render labels)
          cleaningFee: "Cleaning fee",
          weeklyDiscount: "Weekly discount (%)",
          monthlyDiscount: "Monthly discount (%)",
        },

        // ✅ property type options (NEW)
        propertyType: {
          apartment: "Apartment",
          hotel: "Hotel",
          villa: "Villa",
          house: "House",
          studio: "Studio",
        },

        // ✅ amenities (existing + NEW)
        amenities: {
          wifi: "Wi-Fi",
          parking: "Parking",
          pool: "Pool",
          ac: "Air conditioning",
          kitchen: "Kitchen",
          tv: "TV",
          washer: "Washer",
          heating: "Heating",
          elevator: "Elevator",
          seaView: "Sea view",
        },

        // ✅ house rules (NEW)
        houseRules: {
          pets: "Pets allowed",
          smoking: "No smoking",
          checkInFrom: "Check-in from",
          checkInTo: "Check-in to",
          checkOutUntil: "Check-out until",
        },

        // ✅ pricing extras (NEW)
        pricing: {
          cleaningFee: "Cleaning fee (€)",
          weeklyDiscount: "Weekly discount (%)",
          monthlyDiscount: "Monthly discount (%)",
        },

        cta: {
          create: "Create listing",
          creating: "Creating...",
        },

        errors: {
          loginFirst: "Please login first!",
          fillRequired: "Fill all required fields!",
          selectDatesAllRooms: "Select dates for all rooms!",
          setValidPriceAllRooms: "Set a valid price for all rooms!",
          selectAtLeastOneImage: "Select at least one image!",
          grOnly: "Please pick a location in Greece 🇬🇷",
          revError: "Error getting address. Try again.",
        },

        preview: {
          untitled: "Untitled listing",
          pickGreece: "Pick a location in Greece",
          selectAmenities: "Select amenities to show here.",
          missingRequired:
            "Missing required: title, Greece location, ≥ 1 photo, and for every room: price + dates.",
        },
      },

      // ✅ MyProperties page
      myProperties: {
        title: "My properties",
        subtitle: "Manage listings, bookings, pricing, and availability.",

        auth: {
          loginToSee: "Please login to see your properties.",
        },

        empty: {
          noProperties: "You have no properties yet.",
        },

        nonHost: {
          subtitle: "To add and manage listings, switch your account to Host.",
          ctaTitle: "Become a Host",
          ctaSub:
            "Upgrade your account to list properties, manage availability, and see bookings from guests.",
          upgrading: "Upgrading...",
          becomeHost: "Become a Host",
        },

        location: {
          unknown: "Unknown",
        },

        edit: {
          title: "Edit listing",
          fields: {
            title: "Title",
            location: "Location",
            basePriceOptional: "Base price (optional)",
            rating: "Rating",
            description: "Description",
            amenities: "Amenities",
          },
          placeholders: {
            title: "Title",
            locationText: "Location text",
            basePrice: "Base price",
            rating: "Rating",
            description: "Description",
          },
          tips: {
            locationFromAddProperty:
              "Tip: if you switched to location object, edit it from Add Property.",
            guestsUseRoomPrices: "Guests will mainly use room prices.",
          },
        },

        amenities: {
          wifi: "Wi-Fi",
          parking: "Parking",
          pool: "Pool",
        },

        rooms: {
          title: "Rooms",
          legend: "Blue = available, Red = reserved (disabled)",
          addRoom: "Add room",
          roomN: "Room {{n}}",
          fields: {
            type: "Type",
            priceEur: "Price (€)",
          },
          types: {
            single: "Single",
            double: "Double",
            triple: "Triple",
          },
          reservedDays: "Reserved days: {{n}}",
          availableDays: "Available days: {{n}}",
        },

        card: {
          propertyAlt: "Property",
          noTitle: "No title",
          fromPrice: "From €{{price}}",
        },

        expiry: {
          noExpiry: "No expiry",
          active: "Active",
          expiringSoon: "Expiring soon",
          expired: "Expired",
          daysLeft: "{{n}}d left",
          daysAgo: "{{n}}d ago",
        },

        bookings: {
          title: "Bookings",
          renew: "Renew",
          renewTitle: "Renew 30 days",
          loading: "Loading…",
          noneYet: "No bookings yet.",
          unknown: "Unknown",
          na: "N/A",
          roomTypeAndId: "{{type}} · room #{{id}}",
          roomIdOnly: "room #{{id}}",
        },

        actions: {
          close: "Close",
          cancel: "Cancel",
          saveChanges: "Save changes",
          addImages: "Add images",
          remove: "Remove",
          edit: "Edit",
          delete: "Delete",
          deleting: "Deleting...",
          cancelBooking: "Cancel",
        },

        confirm: {
          cancelBooking: "Cancel this booking?",
          deleteProperty: "Delete this property permanently?",
        },

        success: {
          renewed30: "Listing renewed for 30 more days ✅",
        },

        errors: {
          failedWithStatus: "Failed ({{status}})",
          loadErrorWithStatus: "Load error ({{status}})",
          networkError: "Network error",
          networkBecomingHost: "Network error becoming host.",
          cancelFailedWithStatus: "Cancel failed ({{status}})",
          networkCancelBooking: "Network error canceling booking.",
          renewFailedWithStatus: "Renew failed ({{status}})",
          networkRenewProperty: "Network error renewing property.",
          saveFailedWithStatus: "Save failed ({{status}})",
          errorSavingProperty: "Error saving property.",
          deleteFailed: "Delete failed.",
          pleaseLoginAgain: "Please login again.",
          notAllowedDelete: "You are not allowed to delete this property.",
          propertyNotFound: "Property not found.",
          deleteFailedWithStatus: "Delete failed ({{status}})",
          networkDeleteProperty: "Network error deleting property.",
        },
      },

      // ✅ NEW: PropertyDetails page
      propertyDetails: {
        back: "← Back",

        openPhotos: "Open photos",
        viewAllPhotos: "View all photos ({{count}})",

        fromPrice: "From €{{price}}",
        perNight: "/ night",

        roomsAvailability: "Rooms & availability",

        legend: {
          available: "Available",
          selected: "Selected",
          reserved: "Reserved",
          notAvailable: "Not available",
        },

        roomTitle: "{{type}} · Room #{{id}}",
        pricePerNight: "€{{price}}/night",
        roomHint: "Pick blue days. Your selected days turn red. Reserved/grey days are disabled.",

        selectedNights: "Selected: {{n}} night(s)",
        subtotal: "Subtotal: €{{total}}",

        startingFrom: "Starting from",

        form: {
          namePlaceholder: "Your name",
          phonePlaceholder: "Phone number",
        },

        warnings: {
          mustBeLoggedIn: "You must be logged in to reserve.",
          nameMin: "Enter your name (min 2 characters).",
          phoneValid: "Enter a valid phone (min 8 digits).",
          pickAtLeastOne: "Pick at least 1 available day to reserve.",
        },

        summary: {
          nights: "Nights",
          total: "Total",
          sub: "Select dates in one or more rooms on the left.",
        },

        reserve: "Reserve",

        alerts: {
          loginFirst: "Please login first!",
          validNamePhone: "Enter a valid name and phone!",
          selectAtLeastOneDate: "Select at least one date!",
          reserveFailed: "Failed to reserve dates",
          reserveError: "Error reserving dates",
          reservationConfirmed: "Reservation confirmed!\nTotal nights: {{nights}}\nTotal: €{{total}}",
        },

        notFoundTitle: "Property not found",

        actions: {
          close: "Close ✕",
          previous: "Previous",
          next: "Next",
        },

        images: {
          mainAlt: "Main photo",
          sideAlt: "Side photo {{n}}",
          activeAlt: "Active photo",
          thumbAlt: "Thumbnail {{n}}",
          thumbTitle: "Image {{n}}",
        },

        lightbox: {
          counter: "{{current}} / {{total}}",
        },
      },
    },
  },

  el: {
    translation: {
      nav: {
        home: "Αρχική",
        login: "Σύνδεση",
        register: "Εγγραφή",
        logout: "Αποσύνδεση",
        myProperties: "Τα καταλύματά μου",
        addProperty: "Προσθήκη καταλύματος",
        admin: "Admin",
      },

      home: {
        title: "Βρες διαμονή στην Ελλάδα",
        cityPlaceholder: "Πόλη / περιοχή…",
        maxPricePlaceholder: "Μέγ. €/βράδυ",
        chips: {
          all: "Όλα",
          wifi: "Wi-Fi",
          parking: "Πάρκινγκ",
          pool: "Πισίνα",
          new: "Νέο",
          saved: "Αποθηκευμένα",
          more: "Περισσότερα",
        },
        sort: {
          aria: "Ταξινόμηση",
          recommended: "Προτεινόμενα",
          priceAsc: "Τιμή: Χαμηλή → Υψηλή",
          ratingDesc: "Βαθμολογία: Υψηλή → Χαμηλή",
          newest: "Πιο πρόσφατα",
        },
        view: {
          list: "Λίστα",
          map: "Χάρτης",
        },
        availableStays: "Διαθέσιμα καταλύματα",
        resultOne: "αποτέλεσμα",
        resultMany: "αποτελέσματα",
        empty: {
          title: "Δεν βρέθηκαν καταλύματα",
          sub: "Δοκίμασε άλλη περιοχή, φίλτρα ή αύξησε τη μέγιστη τιμή.",
        },
        map: {
          title: "Χάρτης (προσωρινό)",
          sub: "Επόμενο βήμα: πραγματικός χάρτης (Google Maps / Mapbox) + pins.",
        },
      },

      footer: {
        privacy: "Πολιτική Απορρήτου",
      },

      login: {
        title: "Σύνδεση",
        subtitle: "Καλώς ήρθες πίσω. Συνδέσου για να συνεχίσεις.",
        emailPlaceholder: "Email",
        passwordPlaceholder: "Κωδικός",
        loggingIn: "Γίνεται σύνδεση...",
        readyTitle: "Έτοιμο",
        enterEmailPasswordTitle: "Βάλε email και κωδικό.",
        noAccount: "Δεν έχεις λογαριασμό;",
        registerLink: "Εγγραφή",
        errors: {
          enterEmailPassword: "Βάλε email και κωδικό.",
          invalidEmailPassword: "Λάθος email ή κωδικός.",
          checkInput: "Έλεγξε τα στοιχεία σου.",
          tooManyAttempts: "Πάρα πολλές προσπάθειες. Δοκίμασε αργότερα.",
          serverError: "Σφάλμα διακομιστή. Δοκίμασε ξανά.",
          loginFailed: "Η σύνδεση απέτυχε.",
          missingUserPayload: "Η σύνδεση έγινε, αλλά λείπουν δεδομένα χρήστη.",
          cannotConnect: "Δεν μπορεί να γίνει σύνδεση με τον server. Δοκίμασε ξανά.",
        },
      },

      register: {
        title: "Εγγραφή",
        subtitle: "Δημιούργησε λογαριασμό για να αποθηκεύεις και να κάνεις κρατήσεις.",
        emailPlaceholder: "Email",
        passwordPlaceholder: "Κωδικός (8+, 1 κεφαλαίο, 1 αριθμός)",
        registering: "Γίνεται εγγραφή...",
        readyTitle: "Έτοιμο",
        passwordRuleTitle:
          "Ο κωδικός πρέπει να έχει 8+ χαρακτήρες, 1 κεφαλαίο και 1 αριθμό (μόνο γράμματα/αριθμοί).",
        haveAccount: "Έχεις ήδη λογαριασμό;",
        loginLink: "Σύνδεση",
        errors: {
          enterEmailPassword: "Βάλε email και κωδικό.",
          passwordRule:
            "Ο κωδικός πρέπει να είναι 8+ χαρακτήρες και να περιέχει τουλάχιστον 1 ΚΕΦΑΛΑΙΟ και 1 αριθμό (μόνο γράμματα/αριθμοί).",
          registrationFailed: "Η εγγραφή απέτυχε",
          missingUserPayload: "Η εγγραφή έγινε, αλλά λείπουν δεδομένα χρήστη.",
          cannotConnect: "Δεν μπορεί να γίνει σύνδεση με τον server. Δοκίμασε ξανά.",
        },
      },

      addProperty: {
        title: "Δημιουργία νέας καταχώρισης",
        subtitle: "Επεξεργαστής τύπου Airbnb • Μόνο Ελλάδα 🇬🇷",

        // ✅ sections (existing + NEW)
        section: {
          title: "Τίτλος",
          basics: "Βασικά στοιχεία",
          location: "Τοποθεσία",
          description: "Περιγραφή",
          rating: "Βαθμολογία",
          amenities: "Παροχές",
          houseRules: "Κανόνες σπιτιού",
          pricingExtras: "Έξτρα χρεώσεις",
          rooms: "Δωμάτια",
          photos: "Φωτογραφίες",
          previewTitle: "Προεπισκόπηση καταχώρισης",
          previewSubtitle: "Περίπου όπως θα το δουν οι επισκέπτες",
        },

        placeholders: {
          title: "π.χ. Άνετο διαμέρισμα κοντά στη θάλασσα",
          location: "Αναζήτηση στην Ελλάδα: πόλη, περιοχή, διεύθυνση…",
          description: "Πες στους επισκέπτες τι κάνει το κατάλυμά σου ξεχωριστό…",
          rating: "1-5",
          price: "π.χ. 80",
        },

        tips: {
          map: "Tip: Αναζήτησε ή πάτα στον χάρτη για pin. Επιτρέπεται μόνο Ελλάδα.",
        },

        // ✅ labels (existing + NEW)
        labels: {
          selected: "Επιλεγμένο",
          room: "Δωμάτιο",
          roomType: "Τύπος δωματίου",
          pricePerNight: "Τιμή / βράδυ (€)",
          availableDates: "Διαθέσιμες ημερομηνίες",
          pickMultiple: "Επίλεξε πολλές ημέρες",
          addAnotherRoom: "Προσθήκη δωματίου",
          addPhotos: "Προσθήκη φωτογραφιών",
          remove: "Αφαίρεση",
          startingFrom: "Από",
          perNight: "/ βράδυ",
          rooms: "Δωμάτια",
          photos: "Φωτογραφίες",

          // NEW: basics fields
          propertyType: "Τύπος καταλύματος",
          maxGuests: "Μέγιστοι επισκέπτες",
          bedrooms: "Υπνοδωμάτια",
          bathrooms: "Μπάνια",

          // NEW: pricing extras labels (if you render labels)
          cleaningFee: "Χρέωση καθαρισμού",
          weeklyDiscount: "Εβδομαδιαία έκπτωση (%)",
          monthlyDiscount: "Μηνιαία έκπτωση (%)",
        },

        // ✅ property type options (NEW)
        propertyType: {
          apartment: "Διαμέρισμα",
          hotel: "Ξενοδοχείο",
          villa: "Βίλα",
          house: "Σπίτι",
          studio: "Στούντιο",
        },

        // ✅ amenities (existing + NEW)
        amenities: {
          wifi: "Wi-Fi",
          parking: "Πάρκινγκ",
          pool: "Πισίνα",
          ac: "Κλιματισμός",
          kitchen: "Κουζίνα",
          tv: "Τηλεόραση",
          washer: "Πλυντήριο",
          heating: "Θέρμανση",
          elevator: "Ανελκυστήρας",
          seaView: "Θέα θάλασσα",
        },

        // ✅ house rules (NEW)
        houseRules: {
          pets: "Επιτρέπονται κατοικίδια",
          smoking: "Απαγορεύεται το κάπνισμα",
          checkInFrom: "Check-in από",
          checkInTo: "Check-in έως",
          checkOutUntil: "Check-out έως",
        },

        // ✅ pricing extras (NEW)
        pricing: {
          cleaningFee: "Χρέωση καθαρισμού (€)",
          weeklyDiscount: "Εβδομαδιαία έκπτωση (%)",
          monthlyDiscount: "Μηνιαία έκπτωση (%)",
        },

        cta: {
          create: "Δημιουργία",
          creating: "Δημιουργείται...",
        },

        errors: {
          loginFirst: "Παρακαλώ κάνε σύνδεση πρώτα!",
          fillRequired: "Συμπλήρωσε όλα τα υποχρεωτικά πεδία!",
          selectDatesAllRooms: "Επίλεξε ημερομηνίες για όλα τα δωμάτια!",
          setValidPriceAllRooms: "Βάλε έγκυρη τιμή για όλα τα δωμάτια!",
          selectAtLeastOneImage: "Διάλεξε τουλάχιστον 1 φωτογραφία!",
          grOnly: "Διάλεξε τοποθεσία μέσα στην Ελλάδα 🇬🇷",
          revError: "Σφάλμα λήψης διεύθυνσης. Δοκίμασε ξανά.",
        },

        preview: {
          untitled: "Χωρίς τίτλο",
          pickGreece: "Διάλεξε τοποθεσία στην Ελλάδα",
          selectAmenities: "Διάλεξε παροχές για να εμφανιστούν εδώ.",
          missingRequired:
            "Λείπουν: τίτλος, τοποθεσία στην Ελλάδα, ≥ 1 φωτογραφία και για κάθε δωμάτιο: τιμή + ημερομηνίες.",
        },
      },

      // ✅ MyProperties page
      myProperties: {
        title: "Τα καταλύματά μου",
        subtitle: "Διαχείριση αγγελιών, κρατήσεων, τιμών και διαθεσιμότητας.",

        auth: {
          loginToSee: "Παρακαλώ συνδεθείτε για να δείτε τα καταλύματά σας.",
        },

        empty: {
          noProperties: "Δεν έχετε καταχωρήσει ακόμη καταλύματα.",
        },

        nonHost: {
          subtitle:
            "Για να προσθέσετε και να διαχειριστείτε αγγελίες, αλλάξτε τον λογαριασμό σας σε Host.",
          ctaTitle: "Γίνε Host",
          ctaSub:
            "Αναβαθμίστε τον λογαριασμό σας για να καταχωρείτε καταλύματα, να διαχειρίζεστε διαθεσιμότητα και να βλέπετε κρατήσεις.",
          upgrading: "Αναβάθμιση...",
          becomeHost: "Γίνε Host",
        },

        location: {
          unknown: "Άγνωστο",
        },

        edit: {
          title: "Επεξεργασία αγγελίας",
          fields: {
            title: "Τίτλος",
            location: "Τοποθεσία",
            basePriceOptional: "Βασική τιμή (προαιρετικό)",
            rating: "Βαθμολογία",
            description: "Περιγραφή",
            amenities: "Παροχές",
          },
          placeholders: {
            title: "Τίτλος",
            locationText: "Κείμενο τοποθεσίας",
            basePrice: "Βασική τιμή",
            rating: "Βαθμολογία",
            description: "Περιγραφή",
          },
          tips: {
            locationFromAddProperty:
              "Tip: αν χρησιμοποιείτε αντικείμενο τοποθεσίας, αλλάξτε το από τη σελίδα Προσθήκη Καταλύματος.",
            guestsUseRoomPrices: "Οι επισκέπτες συνήθως χρησιμοποιούν τις τιμές ανά δωμάτιο.",
          },
        },

        amenities: {
          wifi: "Wi-Fi",
          parking: "Πάρκινγκ",
          pool: "Πισίνα",
        },

        rooms: {
          title: "Δωμάτια",
          legend: "Μπλε = διαθέσιμο, Κόκκινο = κρατημένο (απενεργοποιημένο)",
          addRoom: "Προσθήκη δωματίου",
          roomN: "Δωμάτιο {{n}}",
          fields: {
            type: "Τύπος",
            priceEur: "Τιμή (€)",
          },
          types: {
            single: "Μονόκλινο",
            double: "Δίκλινο",
            triple: "Τρίκλινο",
          },
          reservedDays: "Κρατημένες μέρες: {{n}}",
          availableDays: "Διαθέσιμες μέρες: {{n}}",
        },

        card: {
          propertyAlt: "Κατάλυμα",
          noTitle: "Χωρίς τίτλο",
          fromPrice: "Από €{{price}}",
        },

        expiry: {
          noExpiry: "Χωρίς λήξη",
          active: "Ενεργό",
          expiringSoon: "Λήγει σύντομα",
          expired: "Έληξε",
          daysLeft: "Απομένουν {{n}}ημ.",
          daysAgo: "πριν από {{n}}ημ.",
        },

        bookings: {
          title: "Κρατήσεις",
          renew: "Ανανέωση",
          renewTitle: "Ανανέωση για 30 ημέρες",
          loading: "Φόρτωση…",
          noneYet: "Δεν υπάρχουν κρατήσεις ακόμη.",
          unknown: "Άγνωστο",
          na: "N/A",
          roomTypeAndId: "{{type}} · δωμάτιο #{{id}}",
          roomIdOnly: "δωμάτιο #{{id}}",
        },

        actions: {
          close: "Κλείσιμο",
          cancel: "Ακύρωση",
          saveChanges: "Αποθήκευση αλλαγών",
          addImages: "Προσθήκη φωτογραφιών",
          remove: "Αφαίρεση",
          edit: "Επεξεργασία",
          delete: "Διαγραφή",
          deleting: "Διαγραφή...",
          cancelBooking: "Ακύρωση",
        },

        confirm: {
          cancelBooking: "Θέλετε να ακυρώσετε αυτή την κράτηση;",
          deleteProperty: "Να διαγραφεί οριστικά αυτό το κατάλυμα;",
        },

        success: {
          renewed30: "Η αγγελία ανανεώθηκε για άλλες 30 ημέρες ✅",
        },

        errors: {
          failedWithStatus: "Αποτυχία ({{status}})",
          loadErrorWithStatus: "Σφάλμα φόρτωσης ({{status}})",
          networkError: "Σφάλμα δικτύου",
          networkBecomingHost: "Σφάλμα δικτύου κατά την αναβάθμιση σε Host.",
          cancelFailedWithStatus: "Η ακύρωση απέτυχε ({{status}})",
          networkCancelBooking: "Σφάλμα δικτύου κατά την ακύρωση κράτησης.",
          renewFailedWithStatus: "Η ανανέωση απέτυχε ({{status}})",
          networkRenewProperty: "Σφάλμα δικτύου κατά την ανανέωση αγγελίας.",
          saveFailedWithStatus: "Η αποθήκευση απέτυχε ({{status}})",
          errorSavingProperty: "Σφάλμα κατά την αποθήκευση αγγελίας.",
          deleteFailed: "Η διαγραφή απέτυχε.",
          pleaseLoginAgain: "Παρακαλώ συνδεθείτε ξανά.",
          notAllowedDelete: "Δεν επιτρέπεται να διαγράψετε αυτό το κατάλυμα.",
          propertyNotFound: "Το κατάλυμα δεν βρέθηκε.",
          deleteFailedWithStatus: "Η διαγραφή απέτυχε ({{status}})",
          networkDeleteProperty: "Σφάλμα δικτύου κατά τη διαγραφή αγγελίας.",
        },
      },

      // ✅ NEW: PropertyDetails page
      propertyDetails: {
        back: "← Πίσω",

        openPhotos: "Άνοιγμα φωτογραφιών",
        viewAllPhotos: "Όλες οι φωτογραφίες ({{count}})",

        fromPrice: "Από €{{price}}",
        perNight: "/ βράδυ",

        roomsAvailability: "Δωμάτια & διαθεσιμότητα",

        legend: {
          available: "Διαθέσιμο",
          selected: "Επιλεγμένο",
          reserved: "Κρατημένο",
          notAvailable: "Μη διαθέσιμο",
        },

        roomTitle: "{{type}} · Δωμάτιο #{{id}}",
        pricePerNight: "€{{price}}/βράδυ",
        roomHint:
          "Διάλεξε μπλε μέρες. Οι επιλεγμένες γίνονται κόκκινες. Κρατημένες/γκρι είναι απενεργοποιημένες.",

        selectedNights: "Επιλεγμένες: {{n}} νύχτες",
        subtotal: "Υποσύνολο: €{{total}}",

        startingFrom: "Από",

        form: {
          namePlaceholder: "Το όνομά σου",
          phonePlaceholder: "Τηλέφωνο",
        },

        warnings: {
          mustBeLoggedIn: "Πρέπει να είσαι συνδεδεμένος για κράτηση.",
          nameMin: "Βάλε όνομα (τουλάχιστον 2 χαρακτήρες).",
          phoneValid: "Βάλε έγκυρο τηλέφωνο (τουλάχιστον 8 ψηφία).",
          pickAtLeastOne: "Διάλεξε τουλάχιστον 1 διαθέσιμη μέρα.",
        },

        summary: {
          nights: "Νύχτες",
          total: "Σύνολο",
          sub: "Επίλεξε ημερομηνίες σε ένα ή περισσότερα δωμάτια αριστερά.",
        },

        reserve: "Κράτηση",

        alerts: {
          loginFirst: "Πρέπει να συνδεθείς πρώτα!",
          validNamePhone: "Βάλε έγκυρο όνομα και τηλέφωνο!",
          selectAtLeastOneDate: "Διάλεξε τουλάχιστον μία ημερομηνία!",
          reserveFailed: "Αποτυχία κράτησης",
          reserveError: "Σφάλμα κατά την κράτηση",
          reservationConfirmed: "Η κράτηση ολοκληρώθηκε!\nΝύχτες: {{nights}}\nΣύνολο: €{{total}}",
        },

        notFoundTitle: "Το κατάλυμα δεν βρέθηκε",

        actions: {
          close: "Κλείσιμο ✕",
          previous: "Προηγούμενο",
          next: "Επόμενο",
        },

        images: {
          mainAlt: "Κύρια φωτογραφία",
          sideAlt: "Πλευρική φωτογραφία {{n}}",
          activeAlt: "Ενεργή φωτογραφία",
          thumbAlt: "Μικρογραφία {{n}}",
          thumbTitle: "Εικόνα {{n}}",
        },

        lightbox: {
          counter: "{{current}} / {{total}}",
        },
      },
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["en", "el"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "hellastay_lang",
    },
  });

export default i18n;
